import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Supplier master (Masters → Suppliers). Vendors we purchase from: DMCs, hotel
 * aggregators, GDS consolidators, transport, activities, insurance, visa
 * houses. See ADR-0016 / ADR-0020 for the standard master shape.
 *
 * `name` is intentionally NOT unique (two DMCs can share a trading name across
 * regions); identity is the unique `code`.
 */

/** Mirrors the Prisma `SupplierKind` enum. */
export const SupplierKindSchema = z.enum([
  'DMC',
  'HOTEL_AGGREGATOR',
  'AIRLINE_CONSOLIDATOR',
  'TRANSPORT',
  'ACTIVITY',
  'INSURANCE',
  'VISA',
  'OTHER',
]);
export type SupplierKind = z.infer<typeof SupplierKindSchema>;

/** Mirrors the Prisma `SupplierRegion` enum. */
export const SupplierRegionSchema = z.enum(['DOMESTIC', 'INTERNATIONAL']);
export type SupplierRegion = z.infer<typeof SupplierRegionSchema>;

/** The three proofs a supplier record can carry, one file each. */
export const SupplierDocumentKindSchema = z.enum(['GSTIN', 'PAN', 'BANK_PROOF']);
export type SupplierDocumentKind = z.infer<typeof SupplierDocumentKindSchema>;

export const SUPPLIER_DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export const SUPPLIER_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

/** Key the upload-init endpoint mints: `suppliers/documents/<uuid>/<filename>`. */
const SupplierDocumentKeySchema = z
  .string()
  .regex(/^suppliers\/documents\/[0-9a-f-]{36}\/[\w.-]{1,120}$/u, 'Invalid document key');

export const SupplierListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  kind: SupplierKindSchema,
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
  /** Loose FK to `currencies` (no Prisma relation); resolve label client-side. */
  defaultCurrencyId: UuidSchema.nullable(),
  creditDays: z.number().int().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type SupplierListItem = z.infer<typeof SupplierListItemSchema>;

export const SupplierListQuerySchema = PaginationQuerySchema.extend({
  kind: SupplierKindSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type SupplierListQuery = z.infer<typeof SupplierListQuerySchema>;

export const SupplierListResponseSchema = paginatedResponseSchema(SupplierListItemSchema);
export type SupplierListResponse = z.infer<typeof SupplierListResponseSchema>;

/** Domestic bank account type. */
export const SUPPLIER_BANK_ACCOUNT_TYPES = ['SAVINGS', 'CURRENT'] as const;
export const SupplierBankAccountTypeSchema = z.enum(SUPPLIER_BANK_ACCOUNT_TYPES);
export type SupplierBankAccountType = z.infer<typeof SupplierBankAccountTypeSchema>;

export const SupplierDetailSchema = SupplierListItemSchema.extend({
  notes: z.string().nullable(),
  // B4.9 enrichment fields
  address: z.string().nullable(),
  website: z.string().nullable(),
  taxIdGstin: z.string().nullable(),
  taxIdPan: z.string().nullable(),
  externalApiCredentialKey: z.string().nullable(),
  region: SupplierRegionSchema.nullable(),
  contractEmail: z.string().nullable(),
  swiftCode: z.string().nullable(),
  gstinDocumentKey: z.string().nullable(),
  panDocumentKey: z.string().nullable(),
  bankProofDocumentKey: z.string().nullable(),
  // International bank details (2026-10-27, all optional).
  bankBeneficiaryName: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankRoutingCode: z.string().nullable(),
  bankIntermediarySwift: z.string().nullable(),
  bankAccountCurrencyId: UuidSchema.nullable(),
  bankAddress: z.string().nullable(),
  // Domestic bank details (2026-10-27, all optional).
  bankIfsc: z.string().nullable(),
  bankName: z.string().nullable(),
  bankBranch: z.string().nullable(),
  bankAccountType: SupplierBankAccountTypeSchema.nullable(),
  bankUpiId: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type SupplierDetail = z.infer<typeof SupplierDetailSchema>;

const SupplierCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(200),
  kind: SupplierKindSchema,
  contactName: z.string().trim().max(120).nullable().optional(),
  contactEmail: z.string().trim().email('Enter a valid email').max(200).nullable().optional(),
  contactPhone: z.string().trim().max(40).nullable().optional(),
  defaultCurrencyId: UuidSchema.nullable().optional(),
  creditDays: z.number().int().min(0).max(365).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  // B4.9 enrichment fields
  address: z.string().trim().max(500).nullable().optional(),
  website: z.string().trim().max(300).nullable().optional(),
  taxIdGstin: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/u, 'Enter a valid 15-character GSTIN')
    .nullable()
    .optional(),
  taxIdPan: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{5}\d{4}[A-Z]$/u, 'Enter a valid 10-character PAN')
    .nullable()
    .optional(),
  region: SupplierRegionSchema.nullable().optional(),
  contractEmail: z.string().trim().email('Enter a valid email').max(200).nullable().optional(),
  // SWIFT/BIC: bank (4) + country (2) + location (2) + optional branch (3).
  swiftCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/u,
      'Enter a valid 8 or 11 character SWIFT code',
    )
    .nullable()
    .optional(),
  gstinDocumentKey: SupplierDocumentKeySchema.nullable().optional(),
  panDocumentKey: SupplierDocumentKeySchema.nullable().optional(),
  bankProofDocumentKey: SupplierDocumentKeySchema.nullable().optional(),
  // International bank details (2026-10-27) - all optional. Account / IBAN
  // and routing code keep their spacing-free upper-case form for the wire.
  bankBeneficiaryName: z.string().trim().max(200).nullable().optional(),
  bankAccountNumber: z
    .string()
    .trim()
    .toUpperCase()
    .transform((v) => v.replace(/\s+/gu, ''))
    .pipe(
      z
        .string()
        .max(40)
        .regex(/^[A-Z0-9]*$/u, 'Letters and digits only'),
    )
    .nullable()
    .optional(),
  bankRoutingCode: z.string().trim().max(40).nullable().optional(),
  bankIntermediarySwift: z
    .string()
    .trim()
    .toUpperCase()
    .regex(
      /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/u,
      'Enter a valid 8 or 11 character SWIFT code',
    )
    .nullable()
    .optional(),
  bankAccountCurrencyId: UuidSchema.nullable().optional(),
  bankAddress: z.string().trim().max(500).nullable().optional(),
  // Domestic bank details (2026-10-27) - all optional. Account holder and
  // account number reuse bankBeneficiaryName / bankAccountNumber above.
  bankIfsc: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/u, 'Enter a valid 11-character IFSC')
    .nullable()
    .optional(),
  bankName: z.string().trim().max(200).nullable().optional(),
  bankBranch: z.string().trim().max(200).nullable().optional(),
  bankAccountType: SupplierBankAccountTypeSchema.nullable().optional(),
  bankUpiId: z
    .string()
    .trim()
    .regex(/^[\w.-]{2,256}@[A-Za-z][A-Za-z0-9]{1,63}$/u, 'Enter a valid UPI ID, e.g. name@okhdfc')
    .nullable()
    .optional(),
  // Secrets Manager key reference. e.g. 'tbo.hotel.username'.
  externalApiCredentialKey: z.string().trim().max(200).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

/** Bulk recategorize action (B4.9 launch-week tool). */
export const SupplierBulkRecategorizeRequestSchema = z.object({
  supplierIds: z.array(UuidSchema).min(1).max(100),
  kind: SupplierKindSchema,
});
export type SupplierBulkRecategorizeRequest = z.infer<typeof SupplierBulkRecategorizeRequestSchema>;

/**
 * What a complete supplier record must carry: contact email + phone always,
 * GSTIN + PAN for a domestic supplier. Blank strings count as missing.
 */
const addSupplierComplianceIssues = (
  v: {
    region?: SupplierRegion | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    taxIdGstin?: string | null;
    taxIdPan?: string | null;
  },
  ctx: z.RefinementCtx,
): void => {
  const require = (
    path: 'region' | 'contactEmail' | 'contactPhone' | 'taxIdGstin' | 'taxIdPan',
    message: string,
  ): void => {
    if (!v[path]?.trim()) ctx.addIssue({ code: 'custom', path: [path], message });
  };
  require('region', 'Select domestic or international');
  require('contactEmail', 'Contact email is required');
  require('contactPhone', 'Contact phone is required');
  if (v.region === 'DOMESTIC') {
    require('taxIdGstin', 'GSTIN is required for a domestic supplier');
    require('taxIdPan', 'PAN is required for a domestic supplier');
  }
};

export const CreateSupplierRequestSchema = SupplierCoreFieldsSchema.superRefine(
  addSupplierComplianceIssues,
);
export type CreateSupplierRequest = z.infer<typeof CreateSupplierRequestSchema>;

/**
 * PATCH stays partial (bulk tools and single-field updates), so the
 * completeness rules cannot run on it - field formats still do.
 */
export const UpdateSupplierRequestSchema = SupplierCoreFieldsSchema.partial();
export type UpdateSupplierRequest = z.infer<typeof UpdateSupplierRequestSchema>;

/**
 * The admin edit form: every field the form owns, with the same completeness
 * rules as create. `code` is read-only after create.
 */
export const SupplierEditFormSchema = SupplierCoreFieldsSchema.omit({
  code: true,
  legacyId: true,
}).superRefine(addSupplierComplianceIssues);
export type SupplierEditForm = z.infer<typeof SupplierEditFormSchema>;

export const SupplierDocumentUploadInitRequestSchema = z.object({
  kind: SupplierDocumentKindSchema,
  filename: z.string().min(1).max(255),
  mimeType: z.enum(SUPPLIER_DOCUMENT_MIME_TYPES, 'Upload a PDF, JPG, PNG or WebP file'),
  sizeBytes: z.number().int().positive().max(SUPPLIER_DOCUMENT_MAX_BYTES, 'Max 10 MB per file'),
});
export type SupplierDocumentUploadInitRequest = z.infer<
  typeof SupplierDocumentUploadInitRequestSchema
>;

export const SupplierDocumentUploadInitResponseSchema = z.object({
  presignedUrl: z.url(),
  /** Send back as the matching `...DocumentKey` field on create / update. */
  s3Key: z.string(),
  expiresAt: IsoDateTimeSchema,
});
export type SupplierDocumentUploadInitResponse = z.infer<
  typeof SupplierDocumentUploadInitResponseSchema
>;
